import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  BookOpen, Rocket, Music, Users, ShieldCheck, DollarSign, Radio,
  Zap, Mail, HelpCircle, Search, ChevronRight, Sparkles,
} from "lucide-react";

/**
 * MeloCraft — User Manual
 * -------------------------------------------------
 * This is the single source of truth for how the site works.
 * When new features ship, add a new subsection under the right chapter
 * (or a new chapter if it's a major area). Keep entries short & plain-spoken.
 *
 * Structure:
 *   MANUAL = [ { id, title, icon, intro, sections: [ { id, h, body: [strings|["h", "body"]] } ] } ]
 */
const MANUAL = [
  {
    id: "start",
    title: "Getting Started",
    icon: Rocket,
    intro: "The 60-second tour. If you only read one chapter, read this one.",
    sections: [
      {
        id: "what-is-melocraft",
        h: "What is MeloCraft?",
        body: [
          "MeloCraft is an artist-first music marketplace. Fans stream a free radio of everyone's top tracks, and buy songs for $0.99 or full albums for $15. Artists keep 100% of every sale (Stripe fees excluded) and pay $2/month to sell.",
          "There's no middleman taking a cut of your music. That's the whole idea.",
        ],
      },
      {
        id: "create-account",
        h: "Create an account",
        body: [
          "Click 'Join MeloCraft' in the top right, pick a username and password. You'll get a verification email — click the link inside to confirm your address. If you don't see it, check spam.",
          "You can browse and listen to the free radio without an account. You only need to sign up to buy music, tip artists, follow, or upload.",
        ],
      },
      {
        id: "the-radio",
        h: "Play the free radio",
        body: [
          "The home page shows a 'Trending Station' — every artist's top tracks, streamed FULL-length and free, no login required, no ads. Hit 'Tune In' or click any track to start playing.",
          "The player sits at the bottom of the screen and follows you as you browse. Skip, pause, and scrub freely. Tap the song's title in the player at any time to jump to the artist's profile — great for grabbing their album or checking their other work.",
          "Note: radio plays FULL songs (that's the point of a radio). To download a track for keeps, buy it for $0.99 from any track row. Downloads are always paid — listening is always free.",
        ],
      },
    ],
  },
  {
    id: "fans",
    title: "For Fans",
    icon: Users,
    intro: "Buy music, tip artists, follow the ones you love, wear the record.",
    sections: [
      {
        id: "browse-buy",
        h: "Browsing & buying",
        body: [
          "The Browse page shows every track and album on the platform, filterable by genre. Click any track to preview 30 seconds. Click 'Buy $0.99' to purchase a full-quality download, or open an album page and buy the whole record for $15.",
          "Payments go through Stripe. After purchase you'll get a receipt in your email with a direct download link, and the track/album shows up under 'My Library' on your dashboard.",
        ],
      },
      {
        id: "tips-follows",
        h: "Tips & follows",
        body: [
          "Every artist page has a 'Tip Jar' button. Send any amount you want — 100% goes to the artist.",
          "Hit 'Follow' on an artist's page to get their new drops in your Follow Feed on the home page. Followed artists' new releases also show up in the Weekly Digest email.",
        ],
      },
      {
        id: "merch",
        h: "Merch",
        body: [
          "Every album cover can be printed on a hat, hoodie, or tee. Visit the Merch page or click 'Wear the record' on any album page.",
          "Merch is print-on-demand through Printful (once enabled) — no inventory, no waste. Shipping takes 5-10 business days.",
        ],
      },
      {
        id: "green-room",
        h: "The Green Room — community chat",
        body: [
          "Every signed-in fan can hang out in #everyone — live chat with the artists you follow, drop reactions on their new tracks, ask questions. Open it from the Chat link in the top nav.",
          "Chats are moderated by AI in the background — slurs, harassment, and obvious spam get blocked before they post. Something slipped through? Hover any message and click the flag icon to report it.",
          "React with any of six emoji (🔥 ❤️ 🎧 💯 👀 😂). Hit @username to mention someone — they'll get a bell notification. Admin pins important messages to the top of the room.",
          "Release-note emails: every time we ship a new update to MeloCraft we email you a short note about what's new. You can turn these off any time from Studio → Profile → Release-note emails, or by clicking Unsubscribe in the email footer.",
        ],
      },
    ],
  },
  {
    id: "artists",
    title: "For Artists",
    icon: Music,
    intro: "Upload, price, get paid. Keep 100%. Up to 150 tracks per artist.",
    sections: [
      {
        id: "melo-mondays",
        h: "Melo Mondays — one free song hidden on every profile",
        body: [
          "Every Monday, every artist gets to give away one song for free. YOU pick which one. Fans have to visit each artist's profile to hunt down the freebie — creating a weekly discovery ritual that drives thousands of profile views across the platform.",
          "Set your Monday pick in Studio → Payouts → 'Melo Mondays' card. You can change it up until Monday morning. Track downloads count as normal — no charge to fans, no charge to you, just pure exposure.",
          "Bonus: on Mondays the home-page radio switches to a Melo Monday Radio — it ONLY plays every artist's free pick. Fans tune in and hear a preview reel of every single freebie waiting on the platform. If they like what they hear, they head to the artist's profile to download.",
        ],
      },
      {
        id: "subscribe",
        h: "Subscribe to sell ($2/mo)",
        body: [
          "To upload and sell music you need the Artist Plan — $2/month. Go to Studio → Subscribe. That's the only fee MeloCraft ever charges you.",
          "You can cancel anytime; your account stays active through the billing period.",
        ],
      },
      {
        id: "stripe-connect",
        h: "Connect Stripe (get paid direct)",
        body: [
          "Artists get paid via Stripe Connect — the money for a sale goes directly from the fan's card to your Stripe account. MeloCraft never holds your money.",
          "Go to Studio → Payouts → 'Connect Stripe' and complete the Stripe onboarding (name, address, bank/debit card, ID). Once approved, payouts land automatically in your bank on Stripe's normal schedule.",
        ],
      },
      {
        id: "uploading",
        h: "Uploading tracks — step by step",
        body: [
          "There are TWO buttons in the New Track form and they run in sequence. Get this order right and it feels obvious — get it wrong and you'll wonder why nothing happens.",
          ["Step 1", "Studio → Upload → 'Audio (mp3, wav, m4a)'. Click the file picker and choose your track from your device."],
          ["Step 2", "Hit the small upload button (arrow icon next to the file picker). This uploads the actual audio to MeloCraft. Wait for the tiny orange '✓ Audio ready' confirmation to appear right below the file picker. That's your green light."],
          ["Step 3", "Now fill in the rest — Title, Album (optional; pick one to add this song to an existing record, or leave as 'Standalone single'), Genre, and toggle Explicit if the track has explicit lyrics."],
          ["Step 4", "Hit 'Publish track' — the big orange button at the bottom. This is the one that actually posts it to the store. Don't hit it before Step 2 finishes or you'll get an 'Upload audio first' error."],
          "Pricing is fixed at $0.99 per track site-wide — no undercutting, no race to the bottom. Artists can upload up to 150 tracks; the founder account has no cap.",
          "iOS users: works in Safari. The file picker accepts audio files directly from your Files app or iCloud Drive.",
          ["Move later", "To move an already-published track into an album afterwards, go to Studio → My Music → find the album card at the top → hit 'Manage songs' → tick your singles → Save. No need to re-upload."],
        ],
      },
      {
        id: "albums",
        h: "Building an album",
        body: [
          ["Step 1", "Studio → Upload → 'New album'. Pick a title, upload cover art (square, 1000x1000+), then click 'Create album'. This makes an empty record — no songs in it yet."],
          ["Step 2", "Upload every song you want on the album as its own single (see 'Uploading tracks' above). Leave the Album dropdown alone during upload — it'll say 'Standalone single'. You'll add them to the album in the next step."],
          ["Step 3", "Studio → My Music. At the top you'll see your albums as cards. Click 'Manage songs' on the album you want to fill."],
          ["Step 4", "Tick every song that belongs on this record. Save. Done. Up to 15 songs per album (founder has no cap)."],
          "Songs already in a different album show up grayed out — you can only be in one album at a time. If you want to move one, open the album it's currently in and untick it first.",
          "Albums are priced at $15 flat. Fans can still buy any single song for $0.99 even if it's on an album — buying the whole album just gets them the discount + a keepsake record.",
          "Mark the album 'Published' when it's ready (or hit publish per-track from My Music). Until then it stays private.",
        ],
      },
      {
        id: "promotion",
        h: "Promote your drop",
        body: [
          "Promotions page has Social Boost and Spotlight Ad packages. Pricing is 'platform cost + $1' — we don't inflate ad rates.",
          "Referrals: every artist gets a personal referral link (Studio → Referrals). Anyone who joins MeloCraft as a paid artist through your link earns you a reward.",
        ],
      },
      {
        id: "profile",
        h: "Your artist profile",
        body: [
          "Studio → Profile lets you set a display name, bio, banner image, and social links. A strong profile converts casual listeners into buyers.",
        ],
      },
      {
        id: "artist-lounge",
        h: "The Artist Lounge (chat)",
        body: [
          "Subscribed artists get a private room — #artist-lounge — inside The Green Room chat. Same live chat interface, but only paid artists and the founder can read + post.",
          "Talk about the craft, coordinate collab releases, share what's working. It's off-limits to fans by design.",
          "You can also drop a track into either room — click the Music icon next to the chat input to open the track picker. Any signed-in listener in the room can hit Play on the preview instantly. Great for announcing a new single without waiting for people to check your profile.",
        ],
      },
    ],
  },
  {
    id: "founder",
    title: "For You (Founder / Admin)",
    icon: ShieldCheck,
    adminOnly: true,   // hides the chapter (sidebar, section, search) unless viewer is admin
    intro: "Everything the admin account can do. This is your control room.",
    sections: [
      {
        id: "founder-perks",
        h: "Your privileges",
        body: [
          "Your account (Marcus_nelson_7@live.com) is the founder account. That means: free Artist Plan (no $2/mo charge), unlimited uploads (no 150-track cap), admin console access, and a verified badge next to your name.",
          "You can still buy, sell, tip, follow, upload, and use the site like any user — just without the artist paywall.",
        ],
      },
      {
        id: "admin-console",
        h: "The Admin Console",
        body: [
          "Click 'Admin' in the top nav (only visible to you). Here you can:",
          ["Users", "See every registered user, verify artists, promote to admin, or delete accounts."],
          ["Tracks & Albums", "Feature tracks on the home page, unpublish anything, edit metadata, or remove copyright-infringing content."],
          ["Payments", "See total revenue, subscriptions, and Stripe activity across the platform."],
          ["Growth Channels", "Wire up a Discord or Telegram webhook so every new drop auto-posts to your community."],
        ],
      },
      {
        id: "weekly-digest",
        h: "Weekly Digest email",
        body: [
          "Admin → 'Send Weekly Digest' triggers a Resend email to every user summarizing new drops, trending tracks, and featured artists. Run it weekly (Sundays or Fridays work well) to bring people back.",
        ],
      },
      {
        id: "release-notes",
        h: "Release-note broadcast (auto-email on republish)",
        body: [
          "Every time you republish MeloCraft, drop a one-liner into Admin → Release notes broadcast (e.g. 'shipped Community Chat, fixed player, tweaked upload cap'). Click 'AI Draft preview' to see what the LLM wrote — you can edit the hint and redraft as many times as you want.",
          "Two publish buttons: 'Publish (no email)' just adds it to the public /releases page. 'Publish + Email All' does that AND fans out to every user who hasn't opted out of release-note emails.",
          "Each email includes a signed one-click unsubscribe link — the user's decision is respected forever after (they can turn it back on in Studio → Profile if they change their mind).",
          "The /releases page is public so anyone (even logged-out) can see the changelog — great for showing artists the platform is alive and iterating.",
        ],
      },
      {
        id: "chat-moderation",
        h: "Community chat — moderation",
        body: [
          "Admin can pin any message (little pin icon on hover) — pinned messages show at the top of the room for everyone. Same icon toggles it off.",
          "Delete any message (trash icon on hover). Deleted messages show as '— message removed —' so the conversation stays readable.",
          "Ban a user: use POST /api/chat/ban/{user_id} with a reason. The user can no longer post in the specified room (or all rooms if you leave room empty).",
          "Reports queue: fans can flag any message. See open reports at GET /api/chat/reports; resolve with POST /api/chat/reports/{id}/resolve. UI for this queue is on the backlog.",
        ],
      },
      {
        id: "brand-kit",
        h: "Brand Kit generator",
        body: [
          "There's a script at /app/backend/generate_brandkit.py that spits out social-ready assets (Instagram square, Twitter header, story graphics) using your logo and taglines. Use it whenever you launch a promotion.",
        ],
      },
    ],
  },
  {
    id: "money",
    title: "Money & Payouts",
    icon: DollarSign,
    intro: "Where every dollar goes. No hidden fees.",
    sections: [
      {
        id: "the-numbers",
        h: "The numbers",
        body: [
          ["Track sale — $0.99", "100% to artist (minus Stripe's ~$0.32 processing fee). MeloCraft gets $0."],
          ["Album sale — $15", "100% to artist (minus Stripe's ~$0.74 processing fee). MeloCraft gets $0."],
          ["Tips", "100% to artist (minus Stripe's fee)."],
          ["Merch", "Cost of goods + Printful's markup + $1 platform margin."],
          ["Artist Plan — $2/mo", "This is the only recurring fee. It funds hosting, streaming, storage, and email."],
          ["Promotion packages", "Whatever the platform (Meta/Google/etc.) charges + $1 flat."],
        ],
      },
      {
        id: "refunds",
        h: "Refunds",
        body: [
          "Digital goods are non-refundable once downloaded (industry standard for MP3/FLAC). Merch refunds follow Printful's policy. Subscriptions can be canceled anytime — remaining time stays active until the period ends.",
        ],
      },
    ],
  },
  {
    id: "faq",
    title: "FAQ & Troubleshooting",
    icon: HelpCircle,
    intro: "Common issues and the quickest fix.",
    sections: [
      {
        id: "email-not-arriving",
        h: "I didn't get my verification / receipt email",
        body: [
          "Check spam / promotions. Emails come from onboarding@resend.dev. If it still isn't there, sign in and go to Profile → 'Resend verification', or email Marcus_nelson_7@live.com.",
        ],
      },
      {
        id: "upload-failing",
        h: "My upload keeps failing",
        body: [
          "MP3, WAV, and FLAC are supported. Max 25 MB per track for now. If the upload spins forever, try a smaller file or a different browser. Chrome and Safari both work.",
        ],
      },
      {
        id: "stripe-connect-issue",
        h: "Stripe onboarding won't finish",
        body: [
          "Stripe requires a legal name, address, DOB, and bank/debit info. If you're stuck, click 'Get Started' inside your real Stripe dashboard first — that unlocks the onboarding flow. Then come back to Studio → Payouts.",
        ],
      },
      {
        id: "cant-play",
        h: "Radio won't play",
        body: [
          "Some browsers block autoplay. Click any track manually to start it. On iOS, tap the play button once to unlock audio for the session.",
        ],
      },
      {
        id: "contact",
        h: "Still stuck?",
        body: [
          "Email Marcus_nelson_7@live.com — usually replies within 24 hours. Include the browser, device, and a screenshot if you can.",
        ],
      },
    ],
  },
];

const LAST_UPDATED = "February 2026";

export default function Manual() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // Non-admins never see admin-only chapters — invisible in sidebar, search, and scroll.
  const visibleManual = useMemo(
    () => MANUAL.filter((c) => !c.adminOnly || isAdmin),
    [isAdmin],
  );
  const [q, setQ] = useState("");
  const [activeChapter, setActiveChapter] = useState(visibleManual[0]?.id || MANUAL[0].id);

  // Filter chapters/sections by the search query
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return visibleManual;
    return visibleManual.map((c) => {
      const sections = c.sections.filter((s) => {
        const inH = s.h.toLowerCase().includes(needle);
        const inBody = s.body.some((b) =>
          Array.isArray(b)
            ? (b[0] + " " + b[1]).toLowerCase().includes(needle)
            : b.toLowerCase().includes(needle)
        );
        return inH || inBody;
      });
      if (!sections.length && !c.title.toLowerCase().includes(needle)) return null;
      return { ...c, sections: sections.length ? sections : c.sections };
    }).filter(Boolean);
  }, [q, visibleManual]);

  // Scroll-spy: highlight the chapter currently in view
  useEffect(() => {
    const handler = () => {
      for (const c of visibleManual) {
        const el = document.getElementById(`chapter-${c.id}`);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.top < 200 && rect.bottom > 200) {
          setActiveChapter(c.id);
          return;
        }
      }
    };
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, [visibleManual]);

  const jump = (id) => {
    const el = document.getElementById(`chapter-${id}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-12 pb-32">
      {/* Header */}
      <div className="border-b border-[#2A2A2A] pb-8 mb-10">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF4500] mb-3 flex items-center gap-2">
          <BookOpen className="h-4 w-4" /> User Manual
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl md:text-7xl tracking-tighter">
          How MeloCraft <span className="text-[#FF4500]">works</span>.
        </h1>
        <p className="font-mono text-sm text-gray-400 mt-4 max-w-2xl leading-relaxed">
          Everything you need — for fans, artists, and admins. Search below or pick a chapter.
          This manual updates every time we ship a new feature.
        </p>
        <div className="font-mono text-[10px] uppercase tracking-widest text-gray-600 mt-3">
          Last updated · {LAST_UPDATED}
        </div>

        {/* Search bar */}
        <div className="mt-8 relative max-w-xl">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <input
            data-testid="manual-search"
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search the manual… (e.g. 'stripe', 'upload', 'refund')"
            className="w-full bg-black border border-[#2A2A2A] focus:border-[#FF4500] outline-none pl-10 pr-4 py-3 font-mono text-sm text-white placeholder:text-gray-600 transition-colors"
          />
        </div>
      </div>

      <div className="grid md:grid-cols-[240px_1fr] gap-10">
        {/* Sidebar TOC */}
        <aside className="hidden md:block">
          <div className="sticky top-24">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-gray-500 mb-4">
              Table of Contents
            </div>
            <nav className="space-y-1">
              {filtered.map((c) => {
                const Icon = c.icon;
                const active = activeChapter === c.id;
                return (
                  <button
                    key={c.id}
                    data-testid={`toc-${c.id}`}
                    onClick={() => jump(c.id)}
                    className={`w-full text-left flex items-center gap-3 px-3 py-2 border-l-2 transition-all font-mono text-xs uppercase tracking-widest ${
                      active
                        ? "border-[#FF4500] text-[#FF4500] bg-[#FF4500]/5"
                        : "border-transparent text-gray-400 hover:text-white hover:border-white/30"
                    }`}
                  >
                    <Icon className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">{c.title}</span>
                  </button>
                );
              })}
            </nav>

            <div className="mt-10 border border-[#2A2A2A] p-4 bg-[#121212]">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF4500] mb-2 flex items-center gap-2">
                <Mail className="h-3 w-3" /> Need a human?
              </div>
              <p className="font-mono text-[11px] text-gray-400 leading-relaxed">
                Email Marcus_nelson_7@live.com — usually replies within 24 hours.
              </p>
            </div>
          </div>
        </aside>

        {/* Main content */}
        <main>
          {filtered.length === 0 && (
            <div data-testid="manual-empty" className="border border-dashed border-[#2A2A2A] p-12 text-center">
              <div className="font-display text-2xl mb-2">Nothing matched "{q}"</div>
              <p className="font-mono text-xs text-gray-500">
                Try a different word, or clear the search to see the full manual.
              </p>
            </div>
          )}

          {filtered.map((c) => {
            const Icon = c.icon;
            return (
              <section
                key={c.id}
                id={`chapter-${c.id}`}
                data-testid={`chapter-${c.id}`}
                className="mb-16 scroll-mt-24"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="h-10 w-10 grid place-items-center bg-[#FF4500]/10 border border-[#FF4500]/30">
                    <Icon className="h-5 w-5 text-[#FF4500]" />
                  </div>
                  <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF4500]">
                    Chapter
                  </div>
                </div>
                <h2 className="font-display font-black text-3xl md:text-5xl tracking-tighter">
                  {c.title}
                </h2>
                <p className="font-mono text-sm text-gray-400 mt-3 max-w-2xl leading-relaxed">
                  {c.intro}
                </p>

                <div className="mt-8 space-y-8">
                  {c.sections.map((s) => (
                    <article
                      key={s.id}
                      id={s.id}
                      data-testid={`section-${s.id}`}
                      className="border-l-2 border-[#2A2A2A] hover:border-[#FF4500] transition-colors pl-6 py-1"
                    >
                      <h3 className="font-display font-bold text-xl md:text-2xl tracking-tight mb-3 flex items-center gap-2">
                        <ChevronRight className="h-4 w-4 text-[#FF4500]" />
                        {s.h}
                      </h3>
                      <div className="space-y-3">
                        {s.body.map((b, i) => {
                          // Body items are either strings or [heading, text] tuples.
                          // We namespace the key with the section id so React keeps state
                          // correctly if the manual is filtered/reordered by search.
                          const key = `${s.id}-${Array.isArray(b) ? b[0] : b.slice(0, 24)}-${i}`;
                          return Array.isArray(b) ? (
                            <div key={key} className="flex gap-3 items-start">
                              <span className="font-mono text-[10px] uppercase tracking-widest text-[#FF4500] mt-1 shrink-0 min-w-[140px]">
                                {b[0]}
                              </span>
                              <p className="font-mono text-sm text-gray-300 leading-relaxed flex-1">
                                {b[1]}
                              </p>
                            </div>
                          ) : (
                            <p key={key} className="font-mono text-sm text-gray-300 leading-relaxed">
                              {b}
                            </p>
                          );
                        })}
                      </div>
                    </article>
                  ))}
                </div>
              </section>
            );
          })}

          {/* Footer CTA */}
          <div className="border border-[#2A2A2A] bg-[#121212] p-8 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF4500] mb-2 flex items-center gap-2">
                <Sparkles className="h-4 w-4" /> Ready to ship?
              </div>
              <div className="font-display font-black text-2xl md:text-3xl tracking-tight">
                You know the site. Now put it to work.
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/browse"
                data-testid="manual-cta-browse"
                className="rounded-none bg-[#FF4500] hover:bg-[#E53E00] text-black font-mono text-xs uppercase tracking-widest px-5 py-3 transition-colors"
              >
                Browse the vault
              </Link>
              <Link
                to="/dashboard"
                data-testid="manual-cta-studio"
                className="rounded-none border border-white/20 hover:border-[#FF4500] hover:text-[#FF4500] font-mono text-xs uppercase tracking-widest px-5 py-3 transition-colors"
              >
                Open studio
              </Link>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
